import {
  Box,
  Button,
  Container,
  Group,
  Paper,
  Stack,
  Text,
  Textarea,
  Title,
  Alert,
  Badge,
  Card,
  Transition,
} from '@mantine/core'
import { IconX, IconBulb } from '@tabler/icons-react'
import { useState, useEffect, useMemo } from 'react'
import { JoyAI } from './components/JoyAI'

// HappyCo brand colors and styling
const HAPPYCO_COLORS = {
  primary: '#635BFF',        // blurple-100 (main brand)
  primaryHover: '#5952E6',   // blurple-110 (hover)
  primaryActive: '#4F49CC',  // blurple-120 (active)
  navy: '#232A5C',           // brand navy
  success: '#00C389',        // green-100
  error: '#E91E1F',          // red-100
  warning: '#FF5207',        // orange-100
  background: '#f8f9fa',     // light gray background
  white: '#FFFFFF',
  lightAccent: '#EFEFFF',    // blurple-10
  mediumAccent: '#E0DEFF',   // blurple-20
}

// HappyCo mobile styles
const mobileStyles = `
  body {
    background-color: ${HAPPYCO_COLORS.background} !important;
    min-height: 100vh !important;
    margin: 0 !important;
  }

  @media (max-width: 768px) {
    .mantine-Container-root {
      padding-left: 12px !important;
      padding-right: 12px !important;
    }
    
    .mantine-Button-root {
      min-height: 48px !important;
      touch-action: manipulation !important;
      font-size: 16px !important;
    }
    
    .mantine-Button-label {
      font-size: 16px !important;
      font-weight: 600 !important;
    }
    
    .mantine-Textarea-input {
      font-size: 16px !important;
      min-height: 120px !important;
      padding: 12px !important;
    }
    
    .mantine-Title-root {
      font-size: 18px !important;
    }
    
    .mantine-Card-root {
      padding: 16px !important;
      margin-bottom: 16px !important;
    }
  }
`

interface CategorizationResult {
  category: string
  summary: string
}

const EXAMPLE_TASKS = [
  { label: 'Customer Call', description: 'Hi, this is Claude with apartment number. 2411, I\'m having a leak, uh, in my bathroom in regards to where the AC is, uh, uh, running, uh, where I guess it\'s dripping water and it\'s, uh, while while it\'s running and he, um, so I don\'t know if there\'s, I think there\'s a leak in the, in the ceiling in my bathroom, where, where it where it runs. So, uh, if you could call me back at 123,\n' +
        ' 456. 7890 um again the apartment number is 2411. Thanks again. Okay bye.' },
  { label: 'Spanish', description: 'Mi lavadora no enciende. ¿Puedes arreglarla?' },
  { label: 'Angry resident', description: 'Yeah this is the 5th time I\'ve called and still no answer, I\'m fed up with this. Call me' },
  { label: 'Multiple issues', description: 'Yes, I know you guys are tired of me calling, but this is real at unit 123.\n' +
        ' At University Drive. Um, Claude has said for the last week and a half ever since you guys came and fixed my central air unit, it\'s been leaking\n' +
        ' Out of the vent underneath, where you guys fixed since before, you guys fixed my central air and it keeps flooding my floor. Now, the water is getting all the way to the middle of my living room. Like, someone needs to come and fix this, because the carpet at the bottom of the stairs is going to start to get molded because the floor keeps getting soaking wet like literally I have towels way down and they\'re soaking wet. They can\'t even. Soak the water up as much as it\'s coming out. Like someone needs to come and fix this. The walls are going to start getting molded and stuff like\n' +
        ' This someone needs to come like today to fix this. Thank you 6123456789 again, it\'s unit 123.\n' +
        ' Claude still hasn\'t fixed my bathroom door upstairs. My kids, keep getting locked in the bathroom, our stuff tissue in the door, I put tape on the door. He said, last week he was going to come and replace the door knob. He still hasn\'t come in and replaced the door. Knob. Someone needs to come and fix that like it\'s\n' +
        ' Can you guys please come today to fix this leak? Thank you.' }
]

function AppContent() {
  const [description, setDescription] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState<CategorizationResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Inject HappyCo mobile styles
  useEffect(() => {
    if (typeof document !== 'undefined' && !document.getElementById('happyco-mobile-styles')) {
      const style = document.createElement('style')
      style.id = 'happyco-mobile-styles'
      style.textContent = mobileStyles
      document.head.appendChild(style)
    }
  }, [])

  const handleCategorize = async () => {
    if (description.trim().length < 40) {
      return
    }

    setIsLoading(true)
    setError(null)
    setResult(null)

    try {
      const formData = new FormData()
      formData.append('description', description)

      // Get backend URL from config or fallback to default
      const config = (window as any).TASK_CATEGORIZER_CONFIG || {}
      const backendUrl = config.BACKEND_URL || 'http://localhost:5090'
      const apiUrl = `${backendUrl}/task/categorize`

      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formData
      })

      if (!response.ok) {
        throw new Error('Failed to categorize Work Order. Please try again later.')
      }

      const data = await response.json()
      setResult(data)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred'
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleExampleClick = (example: { label: string; description: string }) => {
    setDescription(example.description)
    setResult(null)
    setError(null)
  }

  // Computed values for button state
  const { trimmedLength, isReady, characterCount } = useMemo(() => {
    const trimmed = description.trim().length
    const count = description.length
    const ready = trimmed >= 40 && !isLoading
    return {
      trimmedLength: trimmed,
      characterCount: count,
      isReady: ready
    }
  }, [description, isLoading])

  return (
    <Container 
      size="sm" 
      px={{ base: 'md', sm: 'lg' }} 
      py={{ base: 'md', sm: 'lg' }} 
      style={{ position: 'relative', maxWidth: '600px' }}
    >
      {/* Loading Overlay */}
      {isLoading && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(248, 249, 250, 0.95)',
            zIndex: 1000,
            backdropFilter: 'blur(2px)'
          }}
        >
          <Stack align="center" gap="sm">
            <JoyAI 
              variant="chat"
              size={120}
              message="Analyzing..."
            />
            <Text 
              ta="center" 
              size="md" 
              fw={500} 
              c="#232A5C"
              style={{ fontFamily: "'proxima-nova', 'Proxima Nova', sans-serif" }}
            >
              AI Processing...
            </Text>
          </Stack>
        </div>
      )}

      <Stack gap="md">
        {/* Header */}
        <Paper 
          p="sm" 
          radius="md" 
          shadow="sm" 
          style={{ 
            background: `linear-gradient(135deg, ${HAPPYCO_COLORS.navy} 0%, ${HAPPYCO_COLORS.primary} 100%)`,
            color: 'white' 
          }}
        >
          <Title order={1} size="h4" ta="center" style={{ fontFamily: "'proxima-nova', 'Proxima Nova', sans-serif", margin: 0 }}>
            JoyAI Categorizer
          </Title>
        </Paper>

        {/* Examples */}
        <Card shadow="sm" padding="md" radius="md">
          <Stack gap="sm">
            <Title order={3} size="h5" style={{ fontFamily: "'proxima-nova', 'Proxima Nova', sans-serif", color: HAPPYCO_COLORS.navy }}>
              Quick Examples
            </Title>
            <Group gap="xs">
              {EXAMPLE_TASKS.map((example, index) => (
                <Button
                  key={index}
                  variant="light"
                  size="xs"
                  onClick={() => handleExampleClick(example)}
                  disabled={isLoading}
                  style={{ 
                    fontSize: '10px', 
                    padding: '4px 8px',
                    fontFamily: "'proxima-nova', 'Proxima Nova', sans-serif"
                  }}
                >
                  {example.label}
                </Button>
              ))}
            </Group>
          </Stack>
        </Card>

        {/* Main Input */}
        <Card shadow="sm" padding="md" radius="md">
          <Stack gap="md">
            <Title order={2} size="h4" style={{ fontFamily: "'proxima-nova', 'Proxima Nova', sans-serif", color: HAPPYCO_COLORS.navy }}>
              Describe your Work Order
            </Title>
            
            <Textarea
              placeholder="Enter detailed description..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              size="sm"
              style={{ resize: 'none' }}
            />

            <Text size="xs" c="dimmed" style={{ marginTop: '-8px' }}>
              {characterCount < 40 
                ? `At least 40 characters needed (${characterCount}/40)`
                : `${characterCount} characters`
              }
            </Text>

            <Button
              onClick={handleCategorize}
              loading={isLoading}
              size="md"
              leftSection={<IconBulb size={16} />}
              fullWidth
              disabled={isLoading || trimmedLength < 40}
              style={{
                background: isReady 
                  ? `linear-gradient(135deg, ${HAPPYCO_COLORS.navy} 0%, ${HAPPYCO_COLORS.primary} 100%)` 
                  : '#e9ecef',
                color: isReady ? 'white' : '#868e96',
                minHeight: '48px',
                fontFamily: "'proxima-nova', 'Proxima Nova', sans-serif"
              }}
            >
              {isLoading ? 'Analyzing...' : 'Categorize'}
            </Button>
          </Stack>
        </Card>

        {/* Results Area - Always visible with fixed height */}
        <Card 
          shadow="sm" 
          padding="md" 
          radius="md" 
          style={{ 
            minHeight: '200px',
            backgroundColor: result ? HAPPYCO_COLORS.white : HAPPYCO_COLORS.lightAccent,
            border: result 
              ? `2px solid ${HAPPYCO_COLORS.success}` 
              : error 
                ? `2px solid ${HAPPYCO_COLORS.error}`
                : `2px dashed ${HAPPYCO_COLORS.mediumAccent}`,
            borderRadius: '8px'
          }}
        >
          {/* Success Results */}
          {result && (
            <Transition mounted={true} transition="fade" duration={300}>
              {(styles) => (
                <div style={styles}>
                  <Stack gap="md">
                    <Box>
                      <Text fw={600} mb="xs" size="sm" style={{ fontFamily: "'proxima-nova', 'Proxima Nova', sans-serif", color: HAPPYCO_COLORS.navy }}>Category:</Text>
                      <Badge 
                        size="lg" 
                        variant="gradient" 
                        gradient={{ from: HAPPYCO_COLORS.navy, to: HAPPYCO_COLORS.primary }}
                        style={{ fontSize: '11px', padding: '6px 10px', fontFamily: "'proxima-nova', 'Proxima Nova', sans-serif" }}
                      >
                        {result.category}
                      </Badge>
                    </Box>

                    <Box>
                      <Text fw={600} mb="xs" size="sm" style={{ fontFamily: "'proxima-nova', 'Proxima Nova', sans-serif", color: HAPPYCO_COLORS.navy }}>Summary:</Text>
                      <Text 
                        size="sm" 
                        style={{ 
                          lineHeight: 1.4,
                          backgroundColor: HAPPYCO_COLORS.lightAccent,
                          padding: '10px',
                          borderRadius: '8px',
                          border: `1px solid ${HAPPYCO_COLORS.mediumAccent}`,
                          fontFamily: "'proxima-nova', 'Proxima Nova', sans-serif"
                        }}
                      >
                        {result.summary}
                      </Text>
                    </Box>
                  </Stack>
                </div>
              )}
            </Transition>
          )}

          {/* Error Display */}
          {error && (
            <Transition mounted={true} transition="fade" duration={300}>
              {(styles) => (
                <Alert
                  icon={<IconX size={16} />} 
                  title="Categorization Error" 
                  color="red"
                  style={styles}
                >
                  {error}
                </Alert>
              )}
            </Transition>
          )}

          {/* Placeholder when no results */}
          {!result && !error && (
            <div 
              style={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                minHeight: '168px'
              }}
            >
              <Stack align="center" gap="sm">
                <JoyAI variant="happy" size={50} showMessage={false} />
                <Text ta="center" c="dimmed" fw={500} size="sm" style={{ fontFamily: "'proxima-nova', 'Proxima Nova', sans-serif" }}>
                  Results appear here
                </Text>
                <Text ta="center" c="dimmed" size="xs" style={{ fontFamily: "'proxima-nova', 'Proxima Nova', sans-serif" }}>
                  Enter description above
                </Text>
              </Stack>
            </div>
          )}
        </Card>
      </Stack>

      {/* Footer */}
      <Paper p="xs" radius="md" style={{ backgroundColor: HAPPYCO_COLORS.lightAccent, marginTop: '1rem' }}>
        <Text size="xs" ta="center" c="dimmed" style={{ fontFamily: "'proxima-nova', 'Proxima Nova', sans-serif" }}>
          Powered by HappyCo
        </Text>
      </Paper>
    </Container>
  )
}

function App() {
  return <AppContent />
}

export default App